import React, { useContext, useState } from 'react'
import { useTranslation } from 'react-i18next'
import MilkFixedService from '~/api/services/milkFixedPrice'
import notify from '~/components/Notify'
import { b64toBlob, handleErrors } from '~/utils/helpers'
import { TermsWrapper } from './style'
import { ThemeContext } from 'styled-components'
import { Checkbox } from 'semantic-ui-react'
import Dialog from '~/components/Dialog'
import { useNavigate } from 'react-router-dom'
import FatAndProteinService from '~/api/services/fatAndProtein'
import { FixedPricePrefixType } from '~/api/services/dashboard'
import StockExChangeService from '~/api/services/stockExchange'

interface TermsProps {
  settingsData: any
  tradingSlotId: string
  link?: string
  openTermsModal: boolean
  handleClose: () => void
  prefix: string
  hideCloseIcon?: boolean
}

const TermsModal: React.FC<TermsProps> = (props) => {
  const {
    settingsData,
    tradingSlotId,
    link,
    openTermsModal,
    handleClose,
    hideCloseIcon,
    prefix
  } = props
  const { t } = useTranslation()

  const [fileSource, setFileSource] = useState(null)
  const [acceptTerms, setAcceptTerms] = useState(0)
  const navigate = useNavigate()
  const [loadingTerms, setLoadingTerms] = useState(false)
  const [isBusy, setIsBusy] = useState(false)
  const { firstColor } = useContext(ThemeContext)

  function getResponse(fileName) {
    switch (prefix) {
      case FixedPricePrefixType.FatProtein:
        return FatAndProteinService.openTradingRoomFile({ fileName })
      case FixedPricePrefixType.FixedPrice:
        return MilkFixedService.openTradingRoomFile({ fileName })
      case FixedPricePrefixType.StockExchange:
        return StockExChangeService.openTradingRoomFile({ fileName })
      default:
        return MilkFixedService.openTradingRoomFile({ fileName })
    }
  }

  async function handleClickTermItem(fileName) {
    setIsBusy(true)
    try {
      const response = await getResponse(fileName)
      const type = response.data.split(';')[0].split('data:')[1]
      const blob = b64toBlob(response.data, type);
      const blobUrl = URL.createObjectURL(blob);
      setFileSource(blobUrl)
    } catch (error) {
      handleErrors(error)
    }
    setIsBusy(false)
  }

  function renderContent() {
    return <TermsWrapper firstColor={firstColor}>
      <div style={{ textAlign: 'center', marginBottom: 50 }} >
        <div className='note-title'>{t('Note')}</div>
        <div className='title'>{t('Please check the:')}</div>
        {settingsData?.terms.map(term => {
          return term.visible && <div
            onClick={() => handleClickTermItem(term.filename)}
            className='item' key={term.title}
          >
            {term.title}
          </div>
        })}
        <div className='title'>{t('which you can see by clicking on each.')}</div>
        <Checkbox
          label={<label className='highlight-color'>
            {settingsData?.texts?.find(text => text.slug === 'open_trading_slot_text')?.translation || ''}
          </label>}
          checked={acceptTerms === 1}
          onChange={(e, data) => setAcceptTerms(data.checked ? 1 : 0)}
          style={{ marginTop: 20 }}
        />
      </div>
    </TermsWrapper>
  }

  function renderModalPreviewFile() {
    return (
      <Dialog
        open={Boolean(fileSource)}
        handleClose={() => setFileSource(null)}
        textContent={<iframe width='100%' height='100%' src={fileSource}></iframe>}
        textHeader={t('Preview')}
        textClose={t('Close')}
        type={'preview'}
      />
    );
  }

  async function handleSubmitTradingTerms() {
    setLoadingTerms(true)
    try {
      switch (prefix) {
        case FixedPricePrefixType.FatProtein:
          await FatAndProteinService.acceptTradingRooms(tradingSlotId)
          break;
        case FixedPricePrefixType.FixedPrice:
          await MilkFixedService.acceptTradingRooms(tradingSlotId)
          break;
        case FixedPricePrefixType.StockExchange:
          await StockExChangeService.acceptTradingRooms(tradingSlotId)
          break;
        default:
          await MilkFixedService.acceptTradingRooms(tradingSlotId)
          break;
      }
      if (link) {
        navigate(link)
      }
      if (handleClose) {
        handleClose()
      }
    } catch (error) {
      handleErrors(error)
    }
    setLoadingTerms(false)
  }

  function renderAcceptModal() {
    return <Dialog
      isDialogOpen={openTermsModal}
      handleClose={() => {
        setAcceptTerms(0)
        handleClose()
      }}
      hideCloseIcon={hideCloseIcon}
      isLoading={loadingTerms || isBusy}
      disableSubmit={acceptTerms !== 1}
      textHeader={t('Trading terms')}
      textSubmit={t('Enter trading')}
      textClose={hideCloseIcon ? null : t('Cancel process')}
      type={'dialog'}
      handleSubmit={() => handleSubmitTradingTerms()}
      textContent={renderContent()}
    />
  }

  return <>

    {renderModalPreviewFile()}
    {openTermsModal && renderAcceptModal()}
  </>
}
export default TermsModal